/**
 * PowerReviews — third-party review widget integration.
 *
 * Reference: sfcc-website/org_hec/cartridge/templates/default/product/components/powerreviews.isml
 */
import { useEffect, useEffectEvent, useState } from "react";

import { Skeleton } from "~/components/ui/skeleton";
import type { PowerReviewsConfig } from "~/hec/.server/powerreviews-config";
import { Ratings } from "~/hec/components/product/ratings";
import { getAppOrigin } from "~/lib/utils/url";
// Side-effect import: Vite hoists into <head>. Our rules are nested under
// `html` so specificity beats the SDK's runtime stylesheet regardless of order.
import "~/styles/powerreviews-vendor.css";
import { reportClientError } from "~/utils/report-client-error";

import {
  setPowerReviewsRollup,
  setPowerReviewsSdkFailed,
  usePowerReviewsRollup,
  usePowerReviewsSdkFailed,
} from "./power-reviews-rollup";

declare global {
  interface Window {
    pwr?: ((...args: unknown[]) => void) & { q?: unknown[][] };
  }
}

export const pwrHeadlineId = "power-review-headline";

const questionSnippetId = "pr-questionsnippet";
const questionDisplayId = "pr-questiondisplay";
const reviewSnippetId = (productId: string) => `pr-reviewsnippet-${productId}`;
const reviewDisplayId = (productId: string) => `pr-reviewdisplay-${productId}`;

// At most one SDK active per page; tracks the loaded src so duplicate boots no-op.
let bootedSrc: string | null = null;

// One divergence beacon per product per client session — SPA remounts of the
// same PDP must not re-fire it. Tiny and session-scoped, no eviction.
const reportedDivergences = new Set<string>();

/**
 * Minimum review-count gap before a rollup divergence is worth an error row
 * (SFCC-10620). BOTH must be met, so neither big-catalog nor small-catalog
 * products spam:
 *   - absolute guards high-count products (`1834 vs 1835` is 0.05% — noise, and
 *     an absolute-only rule would still let a 10-review gap on a 20k-review
 *     product through);
 *   - relative guards low-count products (`2 vs 3` is 33% — also noise, and a
 *     relative-only rule would fire on every brand-new product).
 * The defect class this exists to catch (`50 vs 180`) clears both easily.
 */
const DIVERGENCE_MIN_ABSOLUTE = 10;
const DIVERGENCE_MIN_RELATIVE = 0.1;

function ensurePwrQueue() {
  if (window.pwr) return;
  // Canonical PR queue snippet — buffers pwr() calls until the SDK loads and
  // drains the queue. Re-installed before every render call in case the SDK
  // hot-replaced or cleared the global.
  window.pwr = Object.assign(
    function (...args: unknown[]) {
      const q = (window.pwr as { q?: unknown[][] }).q;
      ((window.pwr as { q?: unknown[][] }).q = q ?? []).push(args);
    },
    {} as { q?: unknown[][] },
  );
}

function bootSdk(src: string) {
  if (typeof document === "undefined" || bootedSrc === src) return;
  bootedSrc = src;
  ensurePwrQueue();
  if (!document.querySelector(`script[src="${src}"]`)) {
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.crossOrigin = "anonymous";
    script.referrerPolicy = "strict-origin-when-cross-origin";
    script.addEventListener(
      "error",
      () => {
        bootedSrc = null;
        setPowerReviewsSdkFailed(true);
      },
      { once: true },
    );
    document.head.appendChild(script);
  }
}

interface PowerReviewsProps {
  config: PowerReviewsConfig;
  /**
   * Master ID for variants so reviews aggregate across the variation tree
   * (SFRA's `apiProduct = apiProduct.masterProduct`).
   */
  productId: string;
  productName: string;
  /** Site-absolute or root-relative URL to the master product. */
  productUrl: string;
  productImageUrl?: string;
  productDescription?: string;
  productUpc?: string;
  productBrand?: string;
  productPrice?: number;
  productInStock: boolean;
}

export function PowerReviewsWidget({
  config,
  productId,
  productName,
  productUrl,
  productImageUrl,
  productDescription,
  productUpc,
  productBrand,
  productPrice,
  productInStock,
}: PowerReviewsProps) {
  const renderWidget = useEffectEvent(() => {
    // SDK may have wiped the global between mounts (cross-PDP nav, hot reload).
    ensurePwrQueue();
    // The SDK appends into non-empty containers instead of replacing, so any
    // path that re-renders into a reused node (HMR remount, future call sites
    // without per-product keys) would stack stale widgets. Containers are
    // normally freshly mounted and empty, making this a no-op.
    for (const id of [
      reviewSnippetId(productId),
      reviewDisplayId(productId),
      questionSnippetId,
      questionDisplayId,
    ]) {
      document.getElementById(id)?.replaceChildren();
    }
    const absoluteUrl = new URL(productUrl, getAppOrigin()).toString();
    const subject: Record<string, unknown> = {
      "@context": "https://schema.org",
      "@type": "Product",
      name: productName,
      url: absoluteUrl,
      in_stock: productInStock,
    };
    if (productDescription) subject.description = productDescription;
    if (productImageUrl) subject.image_url = productImageUrl;
    if (productUpc) subject.upc = productUpc;
    if (productBrand) subject.brand = productBrand;
    if (productPrice !== undefined) subject.price = productPrice;

    window.pwr?.("render", {
      enable_content_collection_modal: true,
      api_key: config.apiKey,
      locale: config.locale,
      merchant_group_id: config.merchantGroupId,
      merchant_id: config.merchantId,
      page_id: productId,
      review_wrapper_url: `${absoluteUrl}?write-review=true`,
      REVIEW_DISPLAY_SNAPSHOT_TYPE: "SIMPLE",
      ENABLE_CLIENT_SIDE_STRUCTURED_DATA: false,
      // Fires once per rendered component. ReviewSnippet carries the live
      // rollup — for a 0-review product `data` comes back EMPTY, which must
      // still publish (count 0), or stale-attr headers never correct
      // (SFCC-11800).
      on_render: (
        renderConfig?: { component?: string; page_id?: string },
        data?: { review_count?: number; average_rating?: number },
      ) => {
        if (renderConfig?.component !== "ReviewSnippet") return;
        if (renderConfig.page_id !== productId) return;
        setPowerReviewsRollup(productId, {
          reviewCount: data?.review_count ?? 0,
          averageRating: data?.average_rating ?? 0,
        });
      },
      subject,
      components: {
        ReviewSnippet: reviewSnippetId(productId),
        ReviewDisplay: reviewDisplayId(productId),
        QuestionSnippet: questionSnippetId,
        QuestionDisplay: questionDisplayId,
      },
    });
  });

  useEffect(() => {
    bootSdk(config.scriptSrc);
  }, [config.scriptSrc]);

  useEffect(() => {
    renderWidget();
    // useEffectEvent's `renderWidget` is stable; eslint plugin pre-19.2 flags it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  return null;
}

// How long the header shows a skeleton before falling back to the catalog
// attrs. SDK load + PR fetch settles in ~1-3s on ordinary connections; hard
// failures (adblock) fall back faster via the script error signal, so this
// only caps silent hangs (SDK loads but on_render never fires).
const ROLLUP_TIMEOUT_MS = 5000;

/**
 * Header star rating synced to the live PowerReviews rollup (SFCC-11800).
 * Skeleton until the SDK reports via on_render; falls back to the SSR'd
 * catalog-attr values when the SDK script errors (blocked, offline) or the
 * rollup doesn't land within ROLLUP_TIMEOUT_MS. A late rollup still takes
 * over, and a rollup already in the store (SPA revisit) skips the skeleton.
 */
export function PowerReviewsSyncedRatings({
  pageId,
  rating,
  reviewCount,
  enabled,
  onClick,
}: {
  /** page_id the widget renders with — the master id for variants. */
  pageId: string;
  /** Fallbacks from the catalog attrs (c_averageRating / c_totalReviews). */
  rating: number;
  reviewCount: number;
  /**
   * False when the widget will never render (PR config off, set product) —
   * renders the attr values immediately instead of a dead skeleton.
   */
  enabled: boolean;
  onClick?: () => void;
}) {
  const rollup = usePowerReviewsRollup(pageId);
  const sdkFailed = usePowerReviewsSdkFailed();
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    // Today's call site key-remounts per product, so the reset is a no-op
    // safety net — it keeps a per-pageId timer correct for any future call
    // site that doesn't remount.
    setTimedOut(false);
    const timer = setTimeout(() => setTimedOut(true), ROLLUP_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [pageId]);

  // A live rollup contradicting the attrs is the upstream data-quality signal
  // (the SFRA import job only UPserts c_totalReviews) — beacon it once per
  // product per session. Count-only trigger: the attr rating is a rounded
  // aggregate, so rating deltas are noise; both ratings ride along as context.
  //
  // SFCC-10620: this used to fire on ANY count delta, which made it the single
  // highest-volume `level: error` line in production — on 2026-08-15 it was
  // roughly a third of all error rows in fsa-production, and essentially all of
  // it was ±1-6 reviews (`642 vs 648`, `1834 vs 1835`), i.e. expected staleness
  // between the nightly attr import and the live rollup. That noise buried real
  // incidents. Only a MATERIAL divergence is worth an error row now; the
  // genuine defect this is meant to catch looks like `50 vs 180`.
  useEffect(() => {
    if (!rollup) return;
    const delta = Math.abs(rollup.reviewCount - reviewCount);
    const relative = delta / Math.max(rollup.reviewCount, reviewCount, 1);
    if (delta < DIVERGENCE_MIN_ABSOLUTE || relative < DIVERGENCE_MIN_RELATIVE)
      return;
    if (reportedDivergences.has(pageId)) return;
    reportedDivergences.add(pageId);
    reportClientError(
      "powerreviews.rollup_divergence",
      new Error(
        `catalog attrs diverge from live PowerReviews rollup for ${pageId}`,
      ),
      {
        pageId,
        attrReviewCount: reviewCount,
        liveReviewCount: rollup.reviewCount,
        attrRating: rating,
        liveRating: rollup.averageRating,
      },
    );
  }, [rollup, pageId, rating, reviewCount]);

  if (rollup) {
    return (
      <Ratings
        rating={rollup.averageRating}
        reviewCount={rollup.reviewCount}
        size="medium"
        onClick={onClick}
      />
    );
  }
  if (!enabled || sdkFailed || timedOut) {
    return (
      <Ratings
        rating={rating}
        reviewCount={reviewCount}
        size="medium"
        onClick={onClick}
      />
    );
  }
  // Same box as the ratings row in InfoColumnSkeleton — no reflow on resolve.
  return <Skeleton className="h-5 w-32" />;
}

/** Container for the review snippet (star rating + count). */
export function PowerReviewsSnippet({
  productId,
  onClick,
}: {
  productId: string;
  onClick?: () => void;
}) {
  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        aria-label="View customer reviews"
        className="power-reviews relative w-full cursor-pointer bg-transparent p-0 text-left"
        data-testid="pdp-reviews-snippet"
      >
        <div id={reviewSnippetId(productId)} />
      </button>
    );
  }
  return (
    <div
      className="power-reviews relative"
      id={reviewSnippetId(productId)}
      data-testid="pdp-reviews-snippet"
    />
  );
}

/** Heading above the full review display. SFRA `<h2 class="power-review-headline">`. */
export function PowerReviewsHeading({
  children,
}: {
  children: React.ReactNode;
}) {
  return (
    <h2
      id={pwrHeadlineId}
      className="power-reviews power-review-headline min-[544px]:text-[28px] min-[544px]:leading-9 mb-4 mt-[30px] scroll-mt-24 pt-[30px] font-grotesque text-[18px] font-medium uppercase leading-[26px] text-primary"
    >
      {children}
    </h2>
  );
}

export function PowerReviewsDisplay({ productId }: { productId: string }) {
  return (
    <section
      aria-label="Customer Reviews"
      className="power-reviews w-full p-[12px] text-primary"
      data-testid="pdp-reviews-display"
    >
      <div id={reviewDisplayId(productId)} />
    </section>
  );
}

export function PowerReviewsQA() {
  return (
    <section aria-label="Questions & Answers" className="power-reviews w-full">
      <div id={questionSnippetId} />
      <div id={questionDisplayId} />
    </section>
  );
}
